﻿import React, { useMemo, useContext, useState } from 'react';
import Context, { context_type } from '@Util/Context';
import { useTranslation } from 'react-i18next';
import moment from 'moment';
import { generate_unique_id } from '@Util/Utils';
import Startpage_Total_Time_Header from '@Elements/Startpage_Total_Time_Header';
import { Time_Log_Entry } from '@Util/Types';

import Select_With_Label from '@Elements/FormElements/Select_With_Label';
import Timelog_Table from '@Elements/Timelog_Table';
import Edit_Schedule_Entry_Popup from '@Modules/Popups/Edit_Schedule_Entry_Popup';

export default function Timelog_Table_Module(): JSX.Element {
    const [t] = useTranslation();
    const {
        todays_time_log,
        weeks_time_log,
        customers
    }: context_type = useContext(Context);

    const [display_mode, set_display_mode] = useState<number>(0);
    const [filter_customers, set_filter_customers] = useState<string>("All");
    const [selected_log_entry, set_selected_log_entry] = useState<string>("");
    const [edit_mode, set_edit_mode] = useState<boolean>(false);

    const customer_options = useMemo(() => {
        const options = customers?.map(customer => ({ text: customer.name, value: customer.id.toString() })) || [];

        options.unshift({ text: t('Text.All'), value: "All" });

        return options;
    }, [customers]);

    // Display customer data based on filter options
    const filtered_time_logs = useMemo(() => {
        const filtered_logs: Time_Log_Entry[] = filter_customers === "All"
            ? (display_mode === 0 ? todays_time_log : weeks_time_log)
            : (display_mode === 0 ? todays_time_log : weeks_time_log).filter(log => log.customerId === parseInt(filter_customers));

        if (display_mode === 0) {
            return filtered_logs;
        } else {
            const week_days: string[] = ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"];
            const week_logs = week_days.reduce((acc, day) => {
                acc[day] = filtered_logs.filter(log => moment(log.startDate).format('dddd') === day);
                return acc;
            }, {});

            return week_logs;
        }
    }, [display_mode, filter_customers, todays_time_log, weeks_time_log]);

    return (
        <section className="Module Timelog_Table_Module">
            <Startpage_Total_Time_Header
                todays_time_log={todays_time_log}
                weeks_time_log={weeks_time_log}
            />
            <form className="Filter">
                <Select_With_Label
                    id={generate_unique_id()}
                    label={t('Label.DisplayMode')}
                    value={display_mode}
                    onChange={(e) => set_display_mode(parseInt(e.target.value))}
                    error={false}
                    options={[
                        { text: t('Text.Today'), value: 0 },
                        { text: t('Text.ThisWeek'), value: 1 }
                    ]}
                />
                <Select_With_Label
                    id={generate_unique_id()}
                    label={t('Label.Customer')}
                    value={filter_customers}
                    onChange={(e) => set_filter_customers(e.target.value)}
                    error={false}
                    options={customer_options}
                />
            </form>
            {
                display_mode === 0 ?
                    <Timelog_Table
                        logs={filtered_time_logs as Time_Log_Entry[]}
                        set_selected_log_entry={set_selected_log_entry}
                        set_edit_mode={set_edit_mode}
                    /> :
                    Object.keys(filtered_time_logs).map((day: string) =>
                        <div key={generate_unique_id()}>
                            <h2 className="Headline2">{t(day)}</h2>
                            <Timelog_Table
                                logs={filtered_time_logs[day] as Time_Log_Entry[]}
                                set_selected_log_entry={set_selected_log_entry}
                                set_edit_mode={set_edit_mode}
                            />
                        </div>
                    )
            }
            <Edit_Schedule_Entry_Popup
                entry_id={selected_log_entry}
                edit_mode={edit_mode}
                set_edit_mode={set_edit_mode}
            />
        </section>
    )
}